import { Controller } from "stimulus";
import { logEvent } from "utils/analytics";
import Appsignal from "utils/appsignal";
import { csrfFetch } from "utils/fetch";
export default class extends Controller {
    static targets = [
        "cardElement",
        "paymentButtonElement",
        "firstNameInput",
        "lastNameInput",
        "emailInput",
        "paymentError",
        "signupButton",
        "paymentButtonContainer",
        "signupButtonContainer",
        "nameContainer",
        "progress",
        "couponContainer"
    ]

    static values = {
        account: String,
        currency: String,
        publicKey: String,
        intentSecret: String,
        intentType: String,
        amount: Number,
        description: String,
        interval: String,
        trialAmount: { type: Number, default: 0 },
        trialDays: { type: Number, default: 0 },
        country: { type: String, default: 'US' },
        prepareUrl: { type: String, default: '' }
    }

    async connect() {
        const loadingTimeout = setTimeout(() => {
            this.setVisibility("card");
            this.showPaymentError("Sorry! We were unable to start payment. Please try again or <a href='https://support.supercast.com' data-controller='beacon-link' data-action='beacon-link#open'>contact us</a> for help.");
        }, 10000);

        logEvent("Payment - Connecting");
        this.setVisibility("loading");

        await this.waitForStripe();

        this.stripe = window.Stripe(this.publicKeyValue, { stripeAccount: this.accountValue });
        this.elements = this.stripe.elements();
        this.currentPaymentMethod = 'card';

        logEvent("Payment - Stripe Ready");
        this.setupCard();
        await this.setupPaymentButton();
        this.setVisibility(this.currentPaymentMethod);

        logEvent("Payment - Ready");

        this.element.addEventListener('submit', this.submitForm.bind(this));

        clearTimeout(loadingTimeout);
    }

    setupCard() {
        this.card = this.elements.create('card');
        this.card.mount(this.cardElementTarget);
        this.card.addEventListener('change', (e) => {
            if (e.error) {
                logEvent("Payment - Card Error", { message: e.error.message });
                this.paymentErrorTarget.textContent = e.error.message;
                this.paymentErrorTarget.classList.remove('hidden');
                this.signupButtonTarget.disabled = true;
            } else if (e.complete) {
                logEvent("Payment - Card Input Successful");
                this.paymentErrorTarget.textContent = '';
                this.paymentErrorTarget.classList.add('hidden');
                this.signupButtonTarget.disabled = false;
            }
        });
    }

    paymentRequestOptions() {
        // Paid trial: the amount due today is the trial fee, not the recurring price.
        const hasPaidTrial = this.trialAmountValue > 0

        const options = {
            total: {
                label: hasPaidTrial ? 'Due today' : 'Total',
                amount: hasPaidTrial ? this.trialAmountValue : this.amountValue,
            },
            country: this.countryValue,
            currency: this.currencyValue,
            requestPayerName: true,
            requestPayerEmail: true,
            disableWallets: ['link'],
        }

        if (this.intervalValue && (this.intervalValue === 'year' || this.intervalValue === 'month')) {
            const recurringPaymentRequest = {
                paymentDescription: this.descriptionValue,
                managementURL: `https://${window.location.host}/subscriber_v2/subscription`,
                regularBilling: {
                    amount: this.amountValue,
                    label: 'Subscription fee',
                    recurringPaymentIntervalUnit: this.intervalValue,
                    recurringPaymentIntervalCount: 1
                }
            }

            if (hasPaidTrial && this.trialDaysValue > 0) {
                recurringPaymentRequest.trialBilling = {
                    amount: this.trialAmountValue,
                    label: 'Trial',
                    recurringPaymentIntervalUnit: 'day',
                    recurringPaymentIntervalCount: this.trialDaysValue
                }
                // Regular billing starts once the trial ends.
                recurringPaymentRequest.regularBilling.recurringPaymentStartDate =
                    new Date(Date.now() + this.trialDaysValue * 24 * 60 * 60 * 1000)
            }

            options["applePay"] = { recurringPaymentRequest }
        }

        return options;
    }

    async setupPaymentButton() {
        this.paymentRequest = this.stripe.paymentRequest(this.paymentRequestOptions());

        this.paymentButton = this.elements.create('paymentRequestButton', {
            paymentRequest: this.paymentRequest,
        })
        const result = await this.paymentRequest.canMakePayment();
        if (result?.applePay || result?.googlePay) {
            this.canUsePaymentButton = true;
            this.currentPaymentMethod = 'button';
            this.paymentButton.mount(this.paymentButtonElementTarget);
        }

        this.paymentRequest.on('paymentmethod', async (e) => {
            this.paymentButtonPaymentMethod = e.paymentMethod
            const succeeded = await this.fillFormFromPaymentMethod(e);
            e.complete(succeeded ? 'success' : 'fail');
        });

        window.paymentRequest = this.paymentRequest;
    }

    waitForStripe() {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (window.Stripe) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
    }

    ALL_VISIBILITY_ELEMENTS = [
        'cardElementTarget',
        'nameContainerTarget',
        'paymentButtonContainerTarget',
        'signupButtonContainerTarget',
        'progressTarget',
        'couponContainerTarget'
    ]

    VISIBILE_ELEMENTS_BY_MODE = {
        'card': ['cardElementTarget', 'nameContainerTarget', 'signupButtonContainerTarget', 'couponContainerTarget'],
        'button': ['paymentButtonContainerTarget', 'couponContainerTarget'],
        'loading': ['progressTarget']
    }

    setVisibility(mode) {
        logEvent("Payment - Setting Visibility", { mode });

        this.ALL_VISIBILITY_ELEMENTS.forEach((target) => {
            if (this["has" + target.charAt(0).toUpperCase() + target.slice(1)]) {
                this[target].classList.add('hidden');
            }
        });

        this.VISIBILE_ELEMENTS_BY_MODE[mode].forEach((target) => {
            if (this["has" + target.charAt(0).toUpperCase() + target.slice(1)]) {
                this[target].classList.remove('hidden');
            }
        });
    }

    async fillFormFromPaymentMethod(e) {
        const form = this.element;

        if (e.payerName && e.payerName.indexOf(" ") > -1) {
            const trimmedName = e.payerName.trim();
            if (this.hasFirstNameInputTarget) {
                this.firstNameInputTarget.value = trimmedName.substring(0, trimmedName.lastIndexOf(' '));
            }
            if (this.hasLastNameInputTarget) {
                this.lastNameInputTarget.value = trimmedName.substring(trimmedName.lastIndexOf(' ') + 1, trimmedName.length);
            }
        }
        if (this.hasEmailInputTarget && e.payerEmail && e.payerEmail.length > 1 && this.emailInputTarget.value.length === 0) {
            this.emailInputTarget.value = e.payerEmail;
        }

        const nameMissing = (this.hasFirstNameInputTarget && this.firstNameInputTarget.value.length === 0) ||
                            (this.hasLastNameInputTarget && this.lastNameInputTarget.value.length === 0);
        const emailMissing = this.hasEmailInputTarget && this.emailInputTarget.value.length === 0;

        if (nameMissing || emailMissing) {
            // We didn't get enough info from Stripe, show the name input and prevent submission
            if (this.hasNameContainerTarget) {
                this.nameContainerTarget.classList.remove('hidden');
            }
            return false;
        }

        return await this.completeSubscription(form);
    }

    switchToCard(e) {
        logEvent("Payment - Switching to Card");
        e.preventDefault();
        this.currentPaymentMethod = 'card';
        this.setVisibility(this.currentPaymentMethod);
    }

    listenForCompleteUserInfo() {
        const form = this.element;

        form.querySelectorAll("[required]").forEach(el => {
            if (el.value.length < 1 || (el.type == 'checkbox' && !el.checked)) {
                this.addInlineError(el, "This field is required");
            } else {
                this.removeInlineError(el);
            }
        });

        form.querySelectorAll("input[type='number'][min]").forEach(el => {
            if (el.value !== "" && el.valueAsNumber < parseInt(el.min)) {
                this.addInlineError(el, `This field must be at least ${el.min}`);
            } else {
                this.removeInlineError(el);
            }
        });

        // Additionally check e-mails
        // TODO: Move all e-mail inputs to type=email so we don't repeat this logic.
        const email = form.querySelector(".email-input")
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (email) {
            if (!emailRegex.test(email.value.toLowerCase())) {
                this.addInlineError(email, "Email is not a valid email");
            } else { this.removeInlineError(email) }
        }

        form.querySelectorAll("input[type='email'][required]").forEach(el => {
            if (!emailRegex.test(el.value.toLowerCase())) {
                this.addInlineError(el, "Email is not a valid email");
            } else { this.removeInlineError(el) }
        })

        // Validate fields that should not match (to_email and from_email on gift is an example)
        form.querySelectorAll("[data-notmatch]").forEach(el => {
            if (document.querySelector(el.dataset.notmatch).value == el.value) {
                this.addInlineError(el, el.dataset.notmatchMessage || `${el.value} is invalid`);
            }
        });

        // return false when there is no errors
        if (form.querySelector(".inline-error") === null) {
            return false;
        } else { return true; }
    }

    addInlineError(element, errorMessage) {
        const currentError = element.parentNode.querySelector(".inline-error")

        element.classList.add("input-error")
        if (currentError === null) {
            element.insertAdjacentHTML("afterend", `<small class='inline-error text-red-500 text-sm'>${errorMessage}</small>`)
        }
    }

    removeInlineError(element) {
        const currentError = element.parentNode.querySelector(".inline-error")

        if (currentError !== null) { currentError.remove(); }
        element.classList.remove("input-error")
    }

    submitForm(e) {
        if (!e.submitter || !e.submitter.formNoValidate) {
            e.preventDefault();
            this.completeSubscription();
        }
    }

    showPaymentError(message) {
        this.paymentErrorTarget.innerHTML = message;
        this.paymentErrorTarget.classList.remove('hidden');
        this.signupButtonTarget.disabled = false;
        this.completing = false;
        this.setVisibility(this.currentPaymentMethod);
    }

    // Asks the server to create the intent for the amount and payer on the form, for pages that
    // don't render one up front. Returns the intent's secret, or null if it couldn't be created.
    async prepareIntent() {
        const intentIdInput = this.element.querySelector("input[name='intent_id']");
        const name = [
            this.hasFirstNameInputTarget ? this.firstNameInputTarget.value : '',
            this.hasLastNameInputTarget ? this.lastNameInputTarget.value : ''
        ].join(' ').trim();

        const response = await csrfFetch(this.prepareUrlValue, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                amount_cents: this.amountValue,
                name: name,
                email: this.hasEmailInputTarget ? this.emailInputTarget.value : '',
                intent_id: intentIdInput ? intentIdInput.value : ''
            })
        });
        const data = await response.json();

        if (!response.ok) {
            logEvent("Payment - Prepare Error", { status: response.status });
            this.showPaymentError(data.error || "Sorry! We were unable to start payment. Please try again or <a href='https://support.supercast.com' data-controller='beacon-link' data-action='beacon-link#open'>contact us</a> for help.");
            return null;
        }

        if (intentIdInput) {
            intentIdInput.value = data.intent_id;
        }
        return data.client_secret;
    }

    async completeSubscription() {
        try {
            logEvent("Payment - Completing Subscription", { paymentMethod: this.currentPaymentMethod });

            if (this.listenForCompleteUserInfo()) {
                return false;
            }

            // Enter in a field submits the form even though the button is disabled, so
            // a second call must not reach Stripe while the first is in flight.
            if (this.completing) {
                return false;
            }
            this.completing = true;

            this.setVisibility('loading');
            this.signupButtonTarget.disabled = true;

            let paymentMethod;
            if (this.currentPaymentMethod == 'card') {
                paymentMethod = {
                    card: this.card,
                    billing_details: {
                        name: this.firstNameInputTarget.value + ' ' + this.lastNameInputTarget.value
                    }
                }
            } else {
                paymentMethod = this.paymentButtonPaymentMethod.id;
            }

            let intentSecret = this.intentSecretValue;
            if (this.prepareUrlValue) {
                intentSecret = await this.prepareIntent();
                if (!intentSecret) {
                    return false;
                }
            }

            // Confirm the card client-side so that we know we're OK on the server.
            const result = this.intentTypeValue == 'setup' ?
                await this.stripe.confirmCardSetup(intentSecret, { payment_method: paymentMethod }) :
                await this.stripe.confirmCardPayment(intentSecret, { payment_method: paymentMethod, setup_future_usage: 'off_session' });

            if (result.error) {
                this.showPaymentError(result.error.message);
                return false;
            } else {
                this.element.submit();
                return true;
            }
        } catch (error) {
            this.showPaymentError("Sorry! We were unable to complete your purchase. Please try again or <a href='https://support.supercast.com' data-controller='beacon-link' data-action='beacon-link#open'>contact us</a> for help.");
            logEvent("Payment - Error", { message: error.message });
            Appsignal.sendError(error, (span) => {
                span.setAction("completeSubscription");
            });
            return false;
        }
    }
}
